import { useT } from "@magic-md/i18n/react";
import { useMemo } from "react";

interface CsvPreviewProps {
    content: string;
    /** "csv" uses comma; "tsv" uses tab. */
    delimiter?: "," | "\t";
    maxRows?: number;
}

/**
 * Lightweight CSV/TSV preview. Splits on newlines and the chosen delimiter
 * — handles common quoted fields ("foo,bar") but is NOT a full RFC 4180
 * parser; users with complex CSVs should rely on a dedicated tool.
 */
export function CsvPreview({ content, delimiter = ",", maxRows = 500 }: CsvPreviewProps) {
    const t = useT("editor");
    const { headers, rows, total, truncated } = useMemo(() => {
        const lines = content.replace(/\r\n/g, "\n").split("\n").filter((l) => l.length > 0);
        if (lines.length === 0) {
            return { headers: [], rows: [], total: 0, truncated: false };
        }
        const parseLine = (line: string): string[] => {
            const out: string[] = [];
            let cur = "";
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                const ch = line[i];
                if (inQuotes) {
                    if (ch === '"' && line[i + 1] === '"') {
                        cur += '"';
                        i++;
                    } else if (ch === '"') {
                        inQuotes = false;
                    } else {
                        cur += ch;
                    }
                } else {
                    if (ch === '"') inQuotes = true;
                    else if (ch === delimiter) {
                        out.push(cur);
                        cur = "";
                    } else cur += ch;
                }
            }
            out.push(cur);
            return out;
        };
        const headers = parseLine(lines[0]);
        const sliced = lines.slice(1, 1 + maxRows);
        const rows = sliced.map(parseLine);
        return {
            headers,
            rows,
            total: lines.length - 1,
            truncated: lines.length - 1 > maxRows,
        };
    }, [content, delimiter, maxRows]);

    if (headers.length === 0) {
        return (
            <div className="h-full flex items-center justify-center text-[12px] text-muted-foreground">
                {t("preview.emptyFile")}
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-background">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-muted/40">
                <span className="text-[11px] font-medium">{t("preview.tablePreview")}</span>
                <span className="text-[10px] font-mono text-muted-foreground">
                    {t("preview.tableStats", { rows: total, columns: headers.length })}
                    {truncated ? ` · ${t("preview.tableTruncated", { maxRows })}` : ""}
                </span>
            </div>
            <div className="flex-1 overflow-auto">
                <table className="w-full text-[11px] font-mono border-collapse">
                    <thead className="sticky top-0 bg-muted/60 backdrop-blur">
                        <tr>
                            <th className="px-2 py-1 text-left text-[10px] text-muted-foreground border-b border-border w-10">
                                #
                            </th>
                            {headers.map((h, i) => (
                                <th
                                    key={i}
                                    className="px-2 py-1 text-left font-medium border-b border-border whitespace-nowrap"
                                >
                                    {h || <span className="opacity-50">—</span>}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri} className="hover:bg-muted/30">
                                <td className="px-2 py-1 text-[10px] text-muted-foreground border-b border-border/40">
                                    {ri + 1}
                                </td>
                                {headers.map((_, ci) => (
                                    <td
                                        key={ci}
                                        className="px-2 py-1 border-b border-border/40 whitespace-nowrap max-w-[300px] overflow-hidden text-ellipsis"
                                        title={row[ci] ?? ""}
                                    >
                                        {row[ci] ?? <span className="opacity-30">—</span>}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
